import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9omq30senbnioleshqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || '';
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormError(serverMsg || '发送失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '51漫画需要下载APP吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要！51漫画支持网页在线观看，直接通过浏览器打开即可阅读所有漫画，无需下载任何APP或插件，随时随地享受下拉式阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '51漫画收费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51漫画所有漫画资源完全免费，无需付费订阅，用户可以直接免费阅读全站所有漫画内容，没有任何隐藏收费项目。',
        },
      },
      {
        '@type': 'Question',
        name: '51漫画支持哪些阅读方式？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51漫画支持下拉式阅读模式，用户只需向下滑动页面即可连续阅读，操作简单流畅。同时支持网页观看和在线漫画阅读，适配手机和电脑端。',
        },
      },
      {
        '@type': 'Question',
        name: '51漫画多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51漫画每日更新热门漫画资源，连载中的漫画会保持与官方同步的更新频率，确保读者能够在第一时间看到最新章节内容。',
        },
      },
      {
        '@type': 'Question',
        name: '51漫画有哪些类型的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51漫画涵盖热血、恋爱、玄幻、搞笑、悬疑、科幻、都市、冒险、武侠、恐怖、治愈等丰富分类，满足不同读者的阅读口味和偏好。',
        },
      },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51漫画',
    url: 'https://www.51manga.com/',
    description: '51漫画 - 免费在线漫画阅读平台，提供海量漫画免费在线阅读。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@51manga.com',
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 51漫画 | 品牌介绍与联系方式"
        description="51漫画是关于我们页面。了解51漫画品牌介绍、平台口碑评价、联系方式。51漫画致力于提供优质免费的在线漫画阅读服务，每日更新热门漫画大全。"
        keywords="51漫画关于我们,51漫画品牌,漫画平台介绍,51漫画联系方式"
        canonicalUrl="https://www.51manga.com/about"
        schema={faqSchema}
      />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            <section className="mb-14">
              <div className="text-center mb-10">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  关于 51漫画
                </h1>
                <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                  致力于为广大漫画爱好者提供最优质的在线阅读体验
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 bg-background-100 rounded-lg border border-background-200/70 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-book-open-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">海量资源</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    汇集超过1000部热门漫画资源，涵盖热血、恋爱、玄幻、搞笑等丰富类型，满足各类读者的阅读需求。
                  </p>
                </div>
                <div className="p-6 bg-background-100 rounded-lg border border-background-200/70 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-flashlight-line text-2xl text-accent-500"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">极速阅读</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    采用下拉式阅读方式，流畅不卡顿，翻页自然。支持手机和电脑端无缝切换，随时随地畅享阅读乐趣。
                  </p>
                </div>
                <div className="p-6 bg-background-100 rounded-lg border border-background-200/70 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i className="ri-heart-line text-2xl text-secondary-500"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">完全免费</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    所有漫画内容完全免费开放，无需任何付费订阅。坚持为漫画爱好者提供无门槛的优质阅读服务。
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
                品牌故事
              </h2>
              <div className="max-w-3xl mx-auto">
                <div className="p-6 bg-background-100 rounded-lg border border-background-200/70">
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    51漫画成立于2020年，由一群热爱漫画的年轻人共同创立。我们深知漫画爱好者对于优质阅读体验的追求，因此倾力打造了这个免费、便捷、内容丰富的在线漫画阅读平台。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    从最初的几十部漫画起步，到现在拥有超过1000部热门漫画资源，51漫画始终坚持"内容为王、用户至上"的理念。我们与多家国内外知名漫画平台建立了合作关系，确保读者能够在第一时间看到最新、最热的漫画内容。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    平台采用先进的下拉式阅读技术，让读者无需频繁点击翻页，只需向下滑动即可连续阅读。同时，我们的网页端适配了各种设备，无论你是在电脑前还是在手机上，都能获得同样流畅的阅读体验。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    未来，51漫画将继续扩展漫画资源库，优化阅读体验，并计划推出更多个性化功能，如智能推荐、阅读记录同步等，为漫画爱好者提供更加贴心的服务。感谢每一位读者的支持与陪伴，你们的喜爱是我们前进的最大动力！
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-5 bg-background-100 rounded-lg border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    "一直在用51漫画看漫画，更新速度很快，画质也很清晰。下拉式阅读真的超级方便，不用一直点翻页了。强烈推荐给所有漫画爱好者！"
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary-600">王</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-900">王小明</p>
                      <p className="text-[10px] text-foreground-400">资深漫画迷 · 使用3年</p>
                    </div>
                  </div>
                </div>

                <div className="p-5 bg-background-100 rounded-lg border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    "资源非常全！很多在其他地方找不到的漫画这里都有。完全免费这一点真的很良心，希望平台越做越好，会一直支持下去的！"
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-accent-600">李</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-900">李雨桐</p>
                      <p className="text-[10px] text-foreground-400">大学生 · 使用2年</p>
                    </div>
                  </div>
                </div>

                <div className="p-5 bg-background-100 rounded-lg border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    "手机上看体验也很好，网页版不用下载APP，打开浏览器就能看。分类很细致，每次都能很快找到想看的类型，收藏功能也很实用。"
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-secondary-600">张</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-900">张浩然</p>
                      <p className="text-[10px] text-foreground-400">上班族 · 使用1年</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
                常见问题
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {[
                  {
                    q: '51漫画需要下载APP吗？',
                    a: '不需要！51漫画支持网页在线观看，直接通过浏览器打开即可阅读所有漫画，无需下载任何APP或插件，随时随地享受下拉式阅读体验。',
                  },
                  {
                    q: '51漫画收费吗？',
                    a: '51漫画所有漫画资源完全免费，无需付费订阅，用户可以直接免费阅读全站所有漫画内容，没有任何隐藏收费项目。',
                  },
                  {
                    q: '51漫画支持哪些阅读方式？',
                    a: '51漫画支持下拉式阅读模式，用户只需向下滑动页面即可连续阅读，操作简单流畅。同时支持网页观看和在线漫画阅读，适配手机和电脑端。',
                  },
                  {
                    q: '51漫画多久更新一次？',
                    a: '51漫画每日更新热门漫画资源，连载中的漫画会保持与官方同步的更新频率，确保读者能够在第一时间看到最新章节内容。',
                  },
                ].map((faq, index) => (
                  <details key={index} className="group bg-background-100 rounded-lg border border-background-200/70">
                    <summary className="flex items-center justify-between p-4 cursor-pointer">
                      <span className="text-sm font-medium text-foreground-900">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
                联系我们
              </h2>
              <div className="max-w-2xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  <div className="p-4 bg-background-100 rounded-lg border border-background-200/70 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <p className="text-xs text-foreground-500">邮箱</p>
                    <p className="text-sm font-medium text-foreground-900">contact@51manga.com</p>
                  </div>
                  <div className="p-4 bg-background-100 rounded-lg border border-background-200/70 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-customer-service-line text-accent-500"></i>
                    </div>
                    <p className="text-xs text-foreground-500">客服QQ</p>
                    <p className="text-sm font-medium text-foreground-900">800123456</p>
                  </div>
                  <div className="p-4 bg-background-100 rounded-lg border border-background-200/70 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-time-line text-secondary-500"></i>
                    </div>
                    <p className="text-xs text-foreground-500">工作时间</p>
                    <p className="text-sm font-medium text-foreground-900">周一至周日 9:00-21:00</p>
                  </div>
                </div>

                <div className="p-6 bg-background-100 rounded-lg border border-background-200/70">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                  {formStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                        <i className="ri-check-line text-2xl text-accent-500"></i>
                      </div>
                      <p className="text-foreground-900 font-medium">消息已发送！</p>
                      <p className="text-sm text-foreground-500 mt-1">感谢您的反馈，我们会尽快回复。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4" action="https://readdy.ai/api/form/d9omq30senbnioleshqg" method="POST" data-readdy-form="">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
                          <input
                            type="text"
                            id="about-name"
                            name="name"
                            required
                            className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-white text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
                          <input
                            type="email"
                            id="about-email"
                            name="email"
                            required
                            className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-white text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1">留言内容</label>
                        <textarea
                          id="about-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-white text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        ></textarea>
                      </div>
                      <div className="anti-spam-field">
                        <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      </div>
                      <button
                        type="submit"
                        className="w-full px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                      >
                        发送消息
                      </button>
                    </form>
                  )}
                  {formStatus === 'error' && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{formError || '发送失败，请稍后重试'}</p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}